import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { AnimateIn } from "./AnimateIn";
import { CtaSection } from "./CtaSection";
import { Footer } from "./Footer";
import JsonData from "../data/data.json";
import "./ProductPage.css";

const productImages = {
  medilens: "/img/illustrations/medilens-preview.svg",
  mediline: "/img/illustrations/mediline-preview.svg",
  mediagents: "/img/illustrations/mediagents-preview.svg",
};

/* ─── Hero ─── */
const ProductHero = ({ data, slug }) => (
  <section className="pp-hero">
    <div className="section-container">
      <div className="pp-hero-grid">
        <AnimateIn variant="fadeLeft" duration={0.8}>
          <div className="pp-hero-copy">
            <p className="eyebrow">{data.eyebrow}</p>
            <h1 className="pp-hero-title">{data.title}</h1>
            <p className="pp-hero-desc">{data.description}</p>
            <div className="pp-hero-actions">
              <a className="pp-cta-primary" href={data.ctaPrimaryUrl} target="_blank" rel="noopener noreferrer">
                {data.ctaPrimary} →
              </a>
              <a className="pp-cta-secondary" href="#workflow">
                {data.ctaSecondary} ↓
              </a>
            </div>
          </div>
        </AnimateIn>
        <AnimateIn variant="fadeRight" delay={0.2} duration={0.8}>
          <div className="pp-hero-visual">
            <img src={productImages[slug]} alt={data.eyebrow} className="pp-hero-image" />
          </div>
        </AnimateIn>
      </div>
    </div>
  </section>
);

/* ─── Workflow ─── */
const ProductWorkflow = ({ data }) => (
  <section id="workflow" className="pp-workflow">
    <div className="section-container">
      <AnimateIn variant="fadeUp">
        <div className="pp-section-header">
          <p className="eyebrow">{data.eyebrow}</p>
          <h2 className="section-title">{data.title}</h2>
        </div>
      </AnimateIn>
      <div className="pp-steps">
        {data.steps.map((step, i) => (
          <AnimateIn key={i} variant="fadeUp" delay={i * 0.15}>
            <div className="pp-step">
              <div className="pp-step-number">{step.number}</div>
              <div className="pp-step-content">
                <h3 className="pp-step-title">{step.title}</h3>
                <p className="pp-step-desc">{step.description}</p>
              </div>
            </div>
          </AnimateIn>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Features ─── */
const ProductFeatures = ({ features, slug }) => (
  <section className="pp-features">
    {features.map((feat, i) => (
      <div key={i} className={`pp-feat-block${!feat.image ? ' pp-feat-text-only' : ''}`}>
        <div className="section-container">
          <div className="pp-feat-grid">
            <AnimateIn variant="fadeUp" duration={0.7}>
              <div className="pp-feat-copy">
                <h3 className="pp-feat-title">{feat.title}</h3>
                <p className="pp-feat-desc">{feat.description}</p>
              </div>
            </AnimateIn>
            {feat.image && (
              <AnimateIn variant="scaleUp" delay={0.1} duration={0.8}>
                <div className="pp-feat-visual">
                  <img src={feat.image || productImages[slug]} alt={feat.title} className="pp-feat-image" />
                </div>
              </AnimateIn>
            )}
            <AnimateIn variant="fadeUp" delay={0.2}>
              <div className="pp-feat-bottom">
                <ul className="pp-feat-highlights">
                  {feat.highlights.map((h, j) => (
                    <li key={j}>{h}</li>
                  ))}
                </ul>
              </div>
            </AnimateIn>
          </div>
        </div>
      </div>
    ))}
  </section>
);

/* ─── Data Sources ─── */
const ProductDataSources = ({ data }) => {
  if (!data || !data.items?.length) return null;
  return (
    <section className="pp-datasources">
      <div className="section-container">
        <AnimateIn variant="fadeUp">
          <div className="pp-section-header">
            <p className="eyebrow">{data.eyebrow}</p>
            <h2 className="section-title">{data.title}</h2>
          </div>
        </AnimateIn>
        <div className="pp-datasources-grid">
          {data.items.map((src, i) => (
            <AnimateIn key={i} variant="fadeUp" delay={i * 0.1}>
              <a
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                className="pp-datasource-card"
              >
                <span className="pp-datasource-name">{src.name}</span>
                <span className="pp-datasource-desc">{src.description}</span>
              </a>
            </AnimateIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Cross Links ─── */
const ProductCrossLinks = ({ links, allPages }) => {
  if (!links || !allPages) return null;
  const products = links.map((s) => allPages[s]).filter(Boolean);

  return (
    <section className="pp-crosslinks">
      <div className="section-container">
        <AnimateIn variant="fadeUp">
          <div className="pp-section-header">
            <p className="eyebrow">その他のプロダクト</p>
            <h2 className="section-title">他のソリューションもご覧ください</h2>
          </div>
        </AnimateIn>
        <div className="pp-crosslinks-grid">
          {products.map((p, i) => (
            <AnimateIn key={i} variant="fadeUp" delay={i * 0.12}>
              <Link to={`/products/${p.slug}`} className="pp-crosslink-card">
                <div className="pp-crosslink-image-wrap">
                  <img src={productImages[p.slug]} alt={p.hero.eyebrow} className="pp-crosslink-image" />
                </div>
                <div className="pp-crosslink-info">
                  <h3 className="pp-crosslink-name">{p.hero.eyebrow}</h3>
                  <p className="pp-crosslink-tagline">{p.hero.title}</p>
                </div>
              </Link>
            </AnimateIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Page ─── */
const ProductPage = () => {
  const { slug } = useParams();
  const product = JsonData.ProductPages?.[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) return <Navigate to="/" replace />;

  return (
    <>
      <ProductHero data={product.hero} slug={slug} />
      <ProductWorkflow data={product.workflow} />
      <ProductFeatures features={product.features} slug={slug} />
      <ProductDataSources data={product.dataSources} />
      <ProductCrossLinks links={product.crossLinks} allPages={JsonData.ProductPages} />
      <CtaSection data={product.cta} />
      <Footer data={JsonData.Footer} />
    </>
  );
};

export default ProductPage;
